import { Flex, Image, Stack, Text, useToast } from "@chakra-ui/react"
import { Button, Heading } from "DesignSystem/components"
import {
  DisplayModal,
  DisplayModalProps,
} from "Shared/components/DisplayModal/DisplayModal"
import { useLinkedinOAuth } from "UserCrowd/hooks/useLinkedinOAuth"
import React from "react"
import {
  useDeletePanelistIntegration,
  useTrackEvent,
} from "~/api/generated/usabilityhub-components"
import SupportingImage from "./linkedin-promo.png"

type LinkedInDisconnectModalProps = Pick<
  DisplayModalProps,
  "isOpen" | "onClose"
> & {
  source: string
}

export const LinkedInDisconnectModal: React.FC<
  LinkedInDisconnectModalProps
> = ({ isOpen, onClose, source }) => {
  const { mutateAsync: deletePanelistIntegration } =
    useDeletePanelistIntegration({})
  const { disconnectLinkedinOAuth } = useLinkedinOAuth()
  const toast = useToast()
  const { mutate: trackEvent } = useTrackEvent({})

  const handleDisconnect = async () => {
    await deletePanelistIntegration(
      {
        body: {
          integration: "linkedin",
        },
      },
      {
        onSuccess: () => {
          disconnectLinkedinOAuth()

          toast({
            title: "Successfully disconnected",
            description:
              "Researchers will no longer see your LinkedIn account. You can reconnect your account at any time in your profile settings.",
            status: "success",
          })

          trackEvent({
            body: {
              event: "panelist_linkedin_verification_disconnected",
              event_properties: {
                source,
              },
            },
          })
          onClose()
        },
        onError: () => {
          toast({
            title: "Error disconnecting",
            description:
              "There was an issue disconnecting your LinkedIn account, please try again. \n\nIf the problem persists contact support@usercrowd.com",
            status: "error",
          })
        },
      }
    )
  }

  return (
    <DisplayModal isOpen={isOpen} onClose={onClose}>
      <DisplayModal.TwoPaneContent
        topHeight={["300px", "300px", "auto"]}
        content={
          <Flex
            h="full"
            w="full"
            bgColor="darkNeutral.opaque.800"
            borderTopRadius="24px"
            borderBottomRadius={[0, "24px"]}
            justifyContent="center"
            alignItems="center"
          >
            <Image
              src={SupportingImage}
              w="full"
              maxW={["340px", "340px", "380px"]}
              objectFit="contain"
              alt="Researchers prefer LinkedIn verified testers"
            />
          </Flex>
        }
        controls={
          <>
            <Button variant="secondary" onClick={onClose}>
              Stay verified
            </Button>
            <Button variant="primary" onClick={handleDisconnect}>
              Yes, disconnect now
            </Button>
          </>
        }
      >
        <Stack
          gap={4}
          minH={[0, "396px"]}
          justifyContent={["flex-start", "flex-start", "center"]}
        >
          <Heading as="h2" textStyle="ds.heading.emphasized">
            Are you sure you want to disconnect your account?
          </Heading>
          <Text textStyle="ds.paragraph.emphasized">
            By disconnecting your LinkedIn account, you{"\u2019"}ll no longer be
            a verified user and you may limit your opportunities for
            higher-paying, better-matched studies.
          </Text>
          <Text textStyle="ds.paragraph.primary" color="ds.text.subtle">
            If you change your mind, you can reconnect later from your profile
            settings.
          </Text>
        </Stack>
      </DisplayModal.TwoPaneContent>
    </DisplayModal>
  )
}
